import { useAuth } from '../lib/auth/useAuth';
import { useAccount } from '../lib/account/useAccount';

/**
 * Visibility gate for the accounting and bank-account surfaces.
 *
 * Reads the backend's own answer (`accountingEnabled` on
 * `GET /accounts/:accountId`), so there is one definition of who is on the
 * accounting programme rather than a copy of it maintained here. That answer
 * already covers the administered-entity walk-up (an entity inherits its firm's
 * flag) and the staff branch, so this hook does not re-derive either.
 *
 * The super-admin short-circuit stays because it is synchronous: a staff caller
 * gets `'granted'` on the first render rather than waiting for the account
 * fetch. It agrees with the backend, which grants staff too.
 *
 * `accountingEnabled` is resolved only by the account fetch, so the answer is
 * not available on first render. While that fetch is in flight and no
 * synchronous grant applies, the hook returns `'loading'` rather than
 * `'denied'`: every route consumer redirects to `/dashboard` on `'denied'`, so
 * answering `'denied'` early would bounce a member off `/accounting` or
 * `/bank-accounts` on a hard refresh.
 */
export function useAccountingAccess(): 'loading' | 'granted' | 'denied' {
  const { user } = useAuth();
  const { account, isLoading: isAccountLoading } = useAccount();
  if (user?.isSuperAdmin) return 'granted';
  if (account?.accountingEnabled) return 'granted';
  // An absent key reads as false (fail closed), but only once the account has
  // actually loaded - before that, absence means "not fetched yet".
  if (isAccountLoading) return 'loading';
  return 'denied';
}
